import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  HiOutlineSparkles,
  HiOutlineKey,
  HiOutlinePlus,
  HiOutlineTableCells,
  HiOutlineBolt,
  HiOutlineBookOpen,
  HiOutlineRocketLaunch,
  HiOutlineShieldCheck,
  HiOutlineClipboardDocument,
  HiOutlineCheck,
  HiOutlineArrowRight,
} from 'react-icons/hi2';
import cloudApi from '../../../services/cloudApi';
import { useAuth } from '../../../contexts/AuthContext';
import CreateTokenModal from './CreateTokenModal';
import McpUpsellModal from './McpUpsellModal';
import ConnectedApps from '../IntegrationsView/ConnectedApps';

// ============================================
// CSS Animations (injected into document.head)
// ============================================

const mcpViewStyles = `
@keyframes mcpFadeSlideIn {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes mcpFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
@keyframes mcpFlowDotVertical {
  0% { top: 0%; opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { top: calc(100% - 6px); opacity: 0; }
}
`;

if (typeof document !== 'undefined') {
  const styleId = 'mcp-view-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = mcpViewStyles;
    document.head.appendChild(style);
  }
}

const formatRelativeTime = (dateString) => {
  const date = cloudApi.toLocalDate(dateString);
  if (!date) return null;
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
};

const formatDate = (dateString) => {
  const date = cloudApi.toLocalDate(dateString);
  if (!date) return 'N/A';
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
};

const isReadOnlyScopes = (scopes) => Array.isArray(scopes) && scopes.length === 1 && scopes[0] === 'read';

// ============================================
// Content
// ============================================

const CAPABILITIES = [
  {
    icon: HiOutlineTableCells,
    title: 'Analyze & clean your data',
    description:
      'Ask questions across your scraped tables — filter, aggregate, compare runs. Then drop junk columns, rename or retype messy ones, and merge fields. No exporting required.',
    circle: 'bg-blue-50 dark:bg-blue-500/15 border-blue-400/30 group-hover:border-blue-500/50 dark:group-hover:border-blue-400/50',
    iconColor: 'text-blue-500 dark:text-blue-400',
    glow: 'bg-blue-400/25',
    hoverShadow: 'hover:shadow-blue-500/10',
  },
  {
    icon: HiOutlineBolt,
    title: 'Operate your automations',
    description:
      'Run, monitor, and stop automations from the conversation — check on any run and pull the results the moment new data lands.',
    circle: 'bg-cyan-50 dark:bg-cyan-500/15 border-cyan-400/30 group-hover:border-cyan-500/50 dark:group-hover:border-cyan-400/50',
    iconColor: 'text-cyan-500 dark:text-cyan-400',
    glow: 'bg-cyan-400/25',
    hoverShadow: 'hover:shadow-cyan-500/10',
  },
  {
    icon: HiOutlineRocketLaunch,
    title: 'Start new extractions',
    description:
      'Product pages, emails, phone numbers, social links, Google Maps — from a list of URLs or a column in an existing table. Credits are always confirmed before anything runs.',
    circle: 'bg-purple-50 dark:bg-purple-500/15 border-purple-400/30 group-hover:border-purple-500/50 dark:group-hover:border-purple-400/50',
    iconColor: 'text-purple-500 dark:text-purple-400',
    glow: 'bg-purple-400/25',
    hoverShadow: 'hover:shadow-purple-500/10',
  },
  {
    icon: HiOutlineBookOpen,
    title: 'Ask how anything works',
    description:
      'Your assistant can search and read the product docs — extension tools, cloud features, credits — and answer with the exact steps, linking the right page.',
    circle: 'bg-emerald-50 dark:bg-emerald-500/15 border-emerald-400/30 group-hover:border-emerald-500/50 dark:group-hover:border-emerald-400/50',
    iconColor: 'text-emerald-500 dark:text-emerald-400',
    glow: 'bg-emerald-400/25',
    hoverShadow: 'hover:shadow-emerald-500/10',
  },
];

// Extraction leads — scraping is the job people connect an agent for; analysis
// and cleanup are what they discover after the first table lands.
const EXAMPLE_PROMPTS = [
  {
    text: 'Scrape all the products from this store and give me a spreadsheet: example.com',
    tag: 'Extract',
    tagClass: 'text-purple-500 dark:text-purple-400',
    dotClass: 'bg-purple-400',
  },
  {
    text: 'Find contact emails and phone numbers for every company in this list of URLs.',
    tag: 'Extract',
    tagClass: 'text-purple-500 dark:text-purple-400',
    dotClass: 'bg-purple-400',
  },
  {
    text: 'Scrape these product pages every morning and track how prices change.',
    tag: 'Extract',
    tagClass: 'text-purple-500 dark:text-purple-400',
    dotClass: 'bg-purple-400',
  },
  {
    text: 'Run my price-monitor automation and tell me when it finishes.',
    tag: 'Operate',
    tagClass: 'text-cyan-600 dark:text-cyan-400',
    dotClass: 'bg-cyan-400',
  },
  {
    text: 'Which products under $50 have a rating above 4.5?',
    tag: 'Analyze',
    tagClass: 'text-blue-500 dark:text-blue-400',
    dotClass: 'bg-blue-400',
  },
  {
    text: 'Clean up this table: drop the empty columns and merge first + last name into one column.',
    tag: 'Clean',
    tagClass: 'text-cyan-600 dark:text-cyan-400',
    dotClass: 'bg-cyan-400',
  },
  {
    text: 'How do credits work? What does one credit get me?',
    tag: 'Learn',
    tagClass: 'text-emerald-600 dark:text-emerald-400',
    dotClass: 'bg-emerald-400',
  },
];

const CONNECT_TABS = [
  { key: 'claude', label: 'Claude' },
  { key: 'claude-code', label: 'Claude Code' },
  { key: 'other', label: 'Other clients' },
];

const WORKS_WITH = ['Claude', 'Claude Code', 'Cursor', 'Any MCP client'];

// ============================================
// Small building blocks
// ============================================

const SectionLabel = ({ children }) => (
  <div className="inline-flex items-center gap-1.5">
    <span className="w-1 h-1 rounded-full bg-cyan-500/60 dark:bg-cyan-400/60" />
    <p className="text-[10px] uppercase tracking-[0.25em] text-cyan-600/70 dark:text-cyan-400/60 font-semibold">
      {children}
    </p>
  </div>
);

const StepBadge = ({ step }) => (
  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 shadow-sm shadow-cyan-500/30">
    {step}
  </div>
);

const StepConnector = () => (
  <div className="flex justify-center py-1" aria-hidden="true">
    <div className="relative h-8 w-px bg-gray-200 dark:bg-white/[0.08]">
      <div
        className="absolute left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-sm shadow-cyan-400/50"
        style={{ animation: 'mcpFlowDotVertical 2.5s ease-in-out infinite' }}
      />
    </div>
  </div>
);

const CopyButton = ({ text, small }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className={`flex-shrink-0 inline-flex items-center gap-1.5 font-medium rounded-lg transition-colors ${
        small
          ? 'px-2 py-1 text-[10px] text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06]'
          : 'px-2.5 py-1.5 text-xs bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
      }`}
    >
      {copied
        ? <HiOutlineCheck className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
        : <HiOutlineClipboardDocument className="w-3.5 h-3.5" />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
};

const SnippetBlock = ({ label, text }) => (
  <div className="rounded-xl border border-gray-200 dark:border-white/[0.07] bg-white dark:bg-gray-900/60 overflow-hidden">
    <div className="flex items-center justify-between px-3.5 py-1.5 bg-gray-50/80 dark:bg-white/[0.03] border-b border-gray-100 dark:border-white/[0.05]">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{label}</span>
      <CopyButton text={text} small />
    </div>
    <pre className="px-3.5 py-3 text-2xs font-mono text-gray-700 dark:text-gray-300 leading-relaxed overflow-x-auto whitespace-pre-wrap break-all">{text}</pre>
  </div>
);

// ============================================
// Hero chat mockup
// ============================================

const RESULT_ROWS = [
  { width: 'w-3/5', price: '$34.99', rating: '4.9', delay: '2s' },
  { width: 'w-2/5', price: '$41.50', rating: '4.8', delay: '2.15s' },
  { width: 'w-1/2', price: '$28.00', rating: '4.7', delay: '2.3s' },
];

const ChatMockup = () => (
  <div className="relative w-full max-w-md mx-auto" style={{ animation: 'mcpFloat 7s ease-in-out infinite' }}>
    <div
      className="absolute -inset-4 bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-purple-500/10 dark:from-blue-500/15 dark:via-cyan-500/15 dark:to-purple-500/15 rounded-3xl blur-2xl"
      aria-hidden="true"
    />
    <div className="relative rounded-2xl border border-gray-200/70 dark:border-white/[0.08] bg-white/80 dark:bg-gray-900/70 backdrop-blur-xl shadow-xl shadow-gray-200/40 dark:shadow-black/40 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-cyan-400/60 to-transparent" aria-hidden="true" />

      {/* Header */}
      <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-100 dark:border-white/[0.06]">
        <div className="w-6 h-6 rounded-lg p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 flex-shrink-0">
          <div className="w-full h-full bg-white rounded-[5px] p-0.5 flex items-center justify-center">
            <img src="/logo-128.png" alt="" className="w-full h-full" />
          </div>
        </div>
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 truncate">Ultimate Web Scraper</span>
        <span className="ml-auto inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex-shrink-0">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
          </span>
          <span className="text-[9px] font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">Connected</span>
        </span>
      </div>

      {/* Conversation */}
      <div className="px-4 py-4 space-y-3">
        <div className="flex justify-end" style={{ animation: 'mcpFadeSlideIn 0.5s ease-out 0.2s both' }}>
          <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs leading-relaxed shadow-sm">
            Which products under $50 have a rating above 4.5?
          </div>
        </div>

        <div style={{ animation: 'mcpFadeSlideIn 0.5s ease-out 0.9s both' }}>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-50 dark:bg-white/[0.04] border border-gray-200/70 dark:border-white/[0.06]">
            <HiOutlineBolt className="w-3 h-3 text-cyan-500 dark:text-cyan-400" />
            <span className="text-[10px] text-gray-500 dark:text-gray-400">Searching your products table</span>
            <HiOutlineCheck className="w-3 h-3 text-emerald-500" />
          </div>
        </div>

        <div className="space-y-2" style={{ animation: 'mcpFadeSlideIn 0.5s ease-out 1.6s both' }}>
          <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
            Found <span className="font-semibold text-gray-800 dark:text-gray-100">12 products</span> — top rated:
          </p>
          <div className="rounded-xl border border-gray-200/70 dark:border-white/[0.06] divide-y divide-gray-100 dark:divide-white/[0.04] overflow-hidden">
            {RESULT_ROWS.map((row, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-3 py-2 bg-white/60 dark:bg-gray-900/40"
                style={{ animation: `mcpFadeSlideIn 0.4s ease-out ${row.delay} both` }}
              >
                <div className={`h-1.5 ${row.width} rounded-full bg-gray-200 dark:bg-gray-700/70`} />
                <span className="ml-auto text-[10px] font-semibold text-gray-600 dark:text-gray-300 tabular-nums flex-shrink-0">{row.price}</span>
                <span className="text-[10px] font-semibold text-amber-500 tabular-nums flex-shrink-0">★ {row.rating}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ============================================
// Example prompt chip (click to copy)
// ============================================

const PromptChip = ({ prompt }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      title="Click to copy"
      className="group relative text-left px-4 py-3 rounded-2xl rounded-bl-md bg-white/70 dark:bg-white/[0.04] border border-gray-200/70 dark:border-white/[0.06] hover:border-cyan-400/40 dark:hover:border-cyan-400/30 hover:shadow-md hover:shadow-cyan-500/5 transition-all duration-300"
    >
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className={`w-1 h-1 rounded-full ${prompt.dotClass}`} />
        <span className={`text-[9px] font-semibold uppercase tracking-wider ${prompt.tagClass}`}>{prompt.tag}</span>
        <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
          {copied
            ? <><HiOutlineCheck className="w-3 h-3 text-emerald-500" /> Copied</>
            : <><HiOutlineClipboardDocument className="w-3 h-3" /> Copy</>}
        </span>
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">&ldquo;{prompt.text}&rdquo;</p>
    </button>
  );
};

// ============================================
// Main view
// ============================================

const McpView = () => {
  const navigate = useNavigate();
  const { isAdmin, subscription, subscriptionLoading } = useAuth();
  // Connecting AI agents is a paid feature — the backend gates both token
  // creation and OAuth consent with 402 PLAN_REQUIRED. Detect the free plan up
  // front so the CTA sells the upgrade instead of failing after a filled-in form.
  const planName = subscription?.subscription?.plan?.name || 'free';
  const isFreePlan = planName === 'free';
  const setupRef = useRef(null);
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [tokensError, setTokensError] = useState(null);
  const [toast, setToast] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const [revokeConfirmId, setRevokeConfirmId] = useState(null);
  const [revokingId, setRevokingId] = useState(null);
  // Plaintext token from a creation in this session — lets the Connect tabs
  // show real, paste-ready URLs (the token is never retrievable again).
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionTokenId, setSessionTokenId] = useState(null);
  const [activeTab, setActiveTab] = useState('claude');
  // Whether the OAuth authorization server is live (OAUTH_ENABLED on the backend).
  // Detected by probing discovery — 404 means off. When on, Claude connects by
  // sign-in (no token); the token path remains for other clients and scripts.
  const [oauthAvailable, setOauthAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${cloudApi.getApiUrl()}/.well-known/oauth-authorization-server`)
      .then((res) => { if (!cancelled) setOauthAvailable(res.ok); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const loadTokens = useCallback(async () => {
    try {
      setTokensError(null);
      const data = await cloudApi.getMcpTokens();
      setTokens(data);
    } catch (error) {
      console.error('Failed to load MCP tokens:', error);
      setTokensError(error.message || 'Failed to load tokens.');
    } finally {
      setLoadingTokens(false);
    }
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const handleTokenCreated = (result) => {
    setSessionToken(result.token);
    setSessionTokenId(result.tokenRecord?.id || null);
    setToast({ type: 'success', message: `Token "${result.tokenRecord?.name || 'MCP token'}" created.` });
    loadTokens();
  };

  const handleRevoke = async (tokenId) => {
    setRevokingId(tokenId);
    try {
      await cloudApi.revokeMcpToken(tokenId);
      // Revoked token is dead — stop offering it in the Connect snippets.
      if (sessionTokenId === tokenId) {
        setSessionToken(null);
        setSessionTokenId(null);
      }
      setToast({ type: 'success', message: 'Token revoked.' });
      setRevokeConfirmId(null);
      await loadTokens();
    } catch (error) {
      console.error('Failed to revoke token:', error);
      setToast({ type: 'error', message: error.message || 'Failed to revoke token.' });
    } finally {
      setRevokingId(null);
    }
  };

  const scrollToSetup = () => {
    setupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Free workspaces get the upsell; paid ones go straight to the create flow.
  // While the plan is still loading, default to the create flow — the modal's
  // own 402 handling is the backstop if the plan turns out to be free.
  const handleConnectClick = () => {
    if (isFreePlan && !subscriptionLoading) {
      setShowUpsell(true);
    } else {
      setShowCreateModal(true);
    }
  };

  const mcpUrl = cloudApi.getMcpServerUrl();
  const tokenValue = sessionToken || 'YOUR_TOKEN';
  const connectorUrl = `${mcpUrl}/t/${tokenValue}`;
  const claudeCodeCommand = `claude mcp add --transport http ultimate-web-scraper ${mcpUrl} --header "Authorization: Bearer ${tokenValue}"`;
  const otherClientsJson = `{
  "mcpServers": {
    "ultimate-web-scraper": {
      "url": "${mcpUrl}",
      "headers": {
        "Authorization": "Bearer ${tokenValue}"
      }
    }
  }
}`;

  return (
    <div className="relative max-w-5xl mx-auto pb-12 overflow-x-clip">
      {/* Ambient background glows */}
      <div className="absolute -left-24 -top-10 w-72 h-72 bg-blue-500/5 dark:bg-blue-500/10 blur-3xl rounded-full pointer-events-none" aria-hidden="true" />
      <div className="absolute -right-24 top-32 w-72 h-72 bg-purple-500/5 dark:bg-purple-500/10 blur-3xl rounded-full pointer-events-none" aria-hidden="true" />

      {/* Toast notification */}
      {toast && (
        <div className={`sticky top-0 z-30 flex items-center gap-2 px-4 py-3 mb-4 rounded-xl text-sm font-medium backdrop-blur-xl shadow-sm ${
          toast.type === 'success'
            ? 'bg-emerald-50/90 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200/60 dark:border-emerald-700/30'
            : 'bg-red-50/90 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200/60 dark:border-red-700/30'
        }`}>
          {toast.type === 'success' ? (
            <HiOutlineCheck className="w-4 h-4 flex-shrink-0" />
          ) : (
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-auto text-current opacity-50 hover:opacity-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Hero */}
      <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-12 items-center pt-6 sm:pt-10 pb-12 sm:pb-16">
        <div>
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-cyan-500/30 dark:border-cyan-400/30 text-cyan-600 dark:text-cyan-300 mb-5">
            <HiOutlineSparkles className="w-3.5 h-3.5" />
            New · AI integration
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.15] mb-4">
            Chat with your{' '}
            <span className="bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 dark:from-blue-400 dark:via-cyan-400 dark:to-purple-400 bg-clip-text text-transparent">
              scraped data
            </span>
          </h1>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400 leading-relaxed max-w-md mb-6">
            Connect Claude — or any MCP client — to your Ultimate Web Scraper workspace. Query tables, clean messy
            columns, run automations, launch new extractions, and ask how anything works — in plain English.
          </p>

          <div className="flex flex-wrap items-center gap-3 mb-7">
            <button
              onClick={handleConnectClick}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-400 hover:via-cyan-400 hover:to-purple-400 shadow-lg shadow-cyan-500/20 hover:shadow-xl hover:shadow-cyan-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
            >
              <HiOutlineKey className="w-4 h-4" />
              Create your token
            </button>
            <button
              onClick={scrollToSetup}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white rounded-xl border border-gray-200 dark:border-white/[0.08] hover:border-gray-300 dark:hover:border-white/[0.15] hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-all duration-200"
            >
              View setup guide
              <HiOutlineArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[9px] uppercase tracking-[0.25em] font-semibold text-gray-400 dark:text-gray-500 mr-1">Works with</span>
            {WORKS_WITH.map((client) => (
              <span
                key={client}
                className="px-2.5 py-1 rounded-full text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100/80 dark:bg-white/[0.04] border border-gray-200/60 dark:border-white/[0.06]"
              >
                {client}
              </span>
            ))}
          </div>
        </div>

        <ChatMockup />
      </div>

      {/* Setup */}
      <div ref={setupRef} className="scroll-mt-4 mb-14">
        <div className="mb-6">
          <SectionLabel>Get connected</SectionLabel>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight mt-2">
            Two steps to set up
          </h2>
        </div>

        {/* Step 1: Tokens */}
        <div className="rounded-2xl bg-white/70 dark:bg-gray-900/40 backdrop-blur-xl border border-gray-200/60 dark:border-gray-700/40 shadow-sm p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <StepBadge step={1} />
              <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">Create an access token</h3>
            </div>
            <button
              onClick={handleConnectClick}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-white rounded-xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-400 hover:via-cyan-400 hover:to-purple-400 shadow-md shadow-cyan-500/20 hover:shadow-cyan-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex-shrink-0 self-start sm:self-auto"
            >
              <HiOutlinePlus className="w-4 h-4" />
              Create token
            </button>
          </div>

          {loadingTokens ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-cyan-500 dark:border-cyan-400"></div>
              <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading tokens...</span>
            </div>
          ) : tokensError ? (
            <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40">
              <p className="text-sm text-gray-500 dark:text-gray-400">{tokensError}</p>
              <button
                onClick={() => { setLoadingTokens(true); loadTokens(); }}
                className="text-xs font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300"
              >
                Retry
              </button>
            </div>
          ) : tokens.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500/10 via-cyan-500/10 to-purple-500/10 border border-cyan-500/20 flex items-center justify-center mb-3">
                <HiOutlineKey className="w-6 h-6 text-cyan-500 dark:text-cyan-400" />
              </div>
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No tokens yet</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm">
                Create your first token to connect Claude or any other MCP client to this workspace.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {tokens.map((token) => (
                <div key={token.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 first:pt-0 last:pb-0">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-white truncate">{token.name}</span>
                      <span className={`text-2xs px-2 py-0.5 rounded-full border ${
                        isReadOnlyScopes(token.scopes)
                          ? 'bg-gray-100 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700/50'
                          : 'bg-cyan-50 dark:bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 border-cyan-200 dark:border-cyan-500/30'
                      }`}>
                        {isReadOnlyScopes(token.scopes) ? 'Read-only' : 'Full access'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <code className="text-xs font-mono text-gray-500 dark:text-gray-400">{token.tokenPrefix}...</code>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {token.lastUsedAt ? `Last used ${formatRelativeTime(token.lastUsedAt)}` : 'Never used'}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        Created {formatDate(token.createdAt)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    {revokeConfirmId === token.id ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleRevoke(token.id)}
                          disabled={revokingId === token.id}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-500 hover:bg-red-400 text-white transition-colors disabled:opacity-50"
                        >
                          {revokingId === token.id ? 'Revoking...' : 'Revoke?'}
                        </button>
                        <button
                          onClick={() => setRevokeConfirmId(null)}
                          disabled={revokingId === token.id}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setRevokeConfirmId(token.id)}
                        className="px-3 py-1.5 text-xs font-medium rounded-lg text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                      >
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <StepConnector />

        {/* Step 2: Connect a client */}
        <div className="rounded-2xl bg-white/70 dark:bg-gray-900/40 backdrop-blur-xl border border-gray-200/60 dark:border-gray-700/40 shadow-sm p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <StepBadge step={2} />
              <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">Connect your client</h3>
            </div>
            <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-gray-100/80 dark:bg-white/[0.04] border border-gray-200/60 dark:border-white/[0.06] self-start sm:self-auto max-w-full overflow-x-auto">
              {CONNECT_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-200 ${
                    activeTab === tab.key
                      ? 'bg-white dark:bg-white/[0.08] text-gray-900 dark:text-white shadow-sm ring-1 ring-gray-200 dark:ring-white/[0.08]'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* The Claude tab needs no token once OAuth is live — the hint would contradict it. */}
          {!(oauthAvailable && activeTab === 'claude') && (
            sessionToken ? (
              <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400 mb-4">
                <HiOutlineCheck className="w-3.5 h-3.5" />
                Using the token you just created — these snippets are ready to paste.
              </p>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                Snippets use <code className="font-mono">YOUR_TOKEN</code> as a placeholder — create a token in step 1 and it fills in automatically.
              </p>
            )
          )}

          {activeTab === 'claude' && (
            <div className="space-y-4">
              <ol className="space-y-2">
                {(oauthAvailable
                  ? [
                      'Open Claude (web or desktop) and go to Settings → Connectors',
                      'Click "Add custom connector" and paste the URL below',
                      'Click Connect and sign in when prompted — pick your workspace and access level, done',
                    ]
                  : [
                      'Open Claude (web or desktop) and go to Settings → Connectors',
                      'Click "Add custom connector"',
                      'Paste the URL below — the token is embedded, no headers needed',
                    ]
                ).map((step, i) => (
                  <li key={i} className="flex gap-2.5 text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    <span className="text-cyan-600 dark:text-cyan-400 font-semibold flex-shrink-0">{i + 1}.</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ol>
              {oauthAvailable ? (
                <>
                  <SnippetBlock label="Connector URL" text={mcpUrl} />
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    No token needed — Claude gets access through your sign-in, scoped to the workspace you
                    approve, and you can disconnect it anytime from Integrations. Tokens (step 1) are for
                    other clients and scripts.
                  </p>
                </>
              ) : (
                <SnippetBlock label="Connector URL" text={connectorUrl} />
              )}
            </div>
          )}
          {activeTab === 'claude-code' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                Run this command in your terminal to add the server to Claude Code:
              </p>
              {oauthAvailable ? (
                <>
                  <SnippetBlock label="Terminal" text={`claude mcp add --transport http ultimate-web-scraper ${mcpUrl}`} />
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    On first use, run <code className="font-mono">/mcp</code> and authenticate — your browser opens to
                    approve access, no token to copy. For non-interactive setups (CI), use a token instead:{' '}
                    <code className="font-mono break-all">--header "Authorization: Bearer &lt;token&gt;"</code>
                  </p>
                </>
              ) : (
                <SnippetBlock label="Terminal" text={claudeCodeCommand} />
              )}
            </div>
          )}
          {activeTab === 'other' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                For Cursor and other MCP clients that support HTTP servers, add this to your MCP configuration:
              </p>
              <SnippetBlock label="mcp.json" text={otherClientsJson} />
              {oauthAvailable && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Clients that support MCP OAuth can skip the headers block entirely and sign in instead.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Connected apps (OAuth grants) — admin-only preview. Lives here (not in
          Integrations) as the "manage" destination for the Claude card. */}
      {isAdmin && (
        <div className="mb-14">
          <div className="mb-6">
            <SectionLabel>Connected apps</SectionLabel>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight mt-2">
              Apps connected by sign-in
            </h2>
          </div>
          <ConnectedApps />
        </div>
      )}

      {/* Capabilities */}
      <div className="mb-14">
        <div className="mb-6">
          <SectionLabel>Capabilities</SectionLabel>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight mt-2">
            What your AI assistant can do
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {CAPABILITIES.map(({ icon: Icon, title, description, circle, iconColor, glow, hoverShadow }) => (
            <div
              key={title}
              className={`group relative rounded-2xl p-5 bg-white/70 dark:bg-gray-900/40 backdrop-blur-xl border border-gray-200/60 dark:border-gray-700/40 shadow-sm hover:shadow-lg ${hoverShadow} hover:-translate-y-0.5 transition-all duration-300`}
            >
              <div className="relative w-10 h-10 mb-4">
                <div className={`absolute inset-0 ${glow} blur-md rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                <div className={`relative w-10 h-10 rounded-full border flex items-center justify-center transition-colors duration-300 ${circle}`}>
                  <Icon className={`w-[1.125rem] h-[1.125rem] ${iconColor}`} />
                </div>
              </div>
              <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100 mb-1.5">{title}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Example prompts */}
      <div className="mb-10">
        <div className="mb-6">
          <SectionLabel>Try asking</SectionLabel>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight mt-2">
            Prompts to get you started
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {EXAMPLE_PROMPTS.map((prompt) => (
            <PromptChip key={prompt.text} prompt={prompt} />
          ))}
        </div>
      </div>

      {/* Security note */}
      <div className="flex gap-3 px-4 py-3.5 rounded-xl bg-gray-50/80 dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/40">
        <HiOutlineShieldCheck className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
          Tokens grant access to this workspace's cloud data and automations per their access level — treat them like
          passwords and revoke them anytime. Requests are rate-limited, and extractions always require an explicit
          confirmation step and consume credits per your plan.
        </p>
      </div>

      <CreateTokenModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleTokenCreated}
        onPlanRequired={() => { setShowCreateModal(false); setShowUpsell(true); }}
        oauthAvailable={oauthAvailable}
      />

      <McpUpsellModal isOpen={showUpsell} onClose={() => setShowUpsell(false)} />
    </div>
  );
};

export default McpView;
